import { fetchCustomers, fetchCustomerSubscriptions, fetchPlaceTypes, fetchSubscriptionPlans, sellSubscription, ApiError, } from '../../../api/adminApi';
import { t, createScreenHeader, createBackButton, formatDurationFromMinutes, isUnauthorized } from './context';
// A `<label>` wrapper (not a `<div>`) so clicking the caption focuses the
// select — same markup the plan modal's place-type field uses.
function createSelectField(labelText, placeholderText) {
    const wrapper = document.createElement('label');
    wrapper.className = 'admin__field';
    const label = document.createElement('span');
    label.className = 'admin__field-label';
    label.textContent = labelText;
    const select = document.createElement('select');
    select.className = 'admin__input';
    wrapper.append(label, select);
    resetSelect(select, placeholderText);
    return { wrapper, select };
}
// Clears a select back to its disabled placeholder row, so "nothing chosen"
// is always represented by an empty `value` the submit guard can test.
function resetSelect(select, placeholderText) {
    select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = placeholderText;
    placeholder.disabled = true;
    placeholder.selected = true;
    select.appendChild(placeholder);
}
function appendOption(select, value, label) {
    const option = document.createElement('option');
    option.value = String(value);
    option.textContent = label;
    select.appendChild(option);
}
export function showSellSubscriptionScreen(ctx) {
    ctx.screen.innerHTML = '';
    const header = createScreenHeader(t('admin.sellSubscription.title'));
    const content = document.createElement('div');
    content.className = 'admin__content admin__form';
    content.textContent = t('admin.sellSubscription.loading');
    const backButton = createBackButton(ctx, t('admin.sellSubscription.back'));
    ctx.screen.append(header, content, backButton);
    Promise.all([fetchCustomers(), fetchPlaceTypes(), fetchSubscriptionPlans()])
        .then(([customers, placeTypes, plans]) => {
        renderSellSubscriptionContent(ctx, content, customers, placeTypes, plans);
    })
        .catch((err) => {
        if (isUnauthorized(err)) {
            ctx.showLogin();
            return;
        }
        console.error('Failed to load sell subscription data:', err);
        content.textContent = t('admin.sellSubscription.loadError');
    });
}
function renderSellSubscriptionContent(ctx, content, customers, placeTypes, plans) {
    content.innerHTML = '';
    const activePlans = plans.filter((p) => p.isActive);
    const customerField = createSelectField(t('admin.sellSubscription.customerLabel'), t('admin.sellSubscription.customerPlaceholder'));
    for (const customer of customers) {
        appendOption(customerField.select, customer.id, `${customer.surname} ${customer.name}`);
    }
    const customerInfo = document.createElement('div');
    customerInfo.className = 'admin__sell-info';
    customerInfo.hidden = true;
    const placeTypeField = createSelectField(t('admin.sellSubscription.placeTypeLabel'), t('admin.sellSubscription.placeTypePlaceholder'));
    for (const placeType of placeTypes) {
        appendOption(placeTypeField.select, placeType.id, placeType.name);
    }
    const planField = createSelectField(t('admin.sellSubscription.planLabel'), t('admin.sellSubscription.planPlaceholder'));
    // Errors from the sale itself surface inside the confirmation modal, and a
    // failed subscription lookup is reported in the panel it belongs to — so the
    // form carries only a success line of its own.
    const success = document.createElement('p');
    success.className = 'admin__success';
    success.hidden = true;
    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'cta admin__submit';
    submit.textContent = t('admin.sellSubscription.submit');
    submit.disabled = true;
    content.append(customerField.wrapper, customerInfo, placeTypeField.wrapper, planField.wrapper, success, submit);
    function updateSubmitState() {
        submit.disabled = !customerField.select.value || !placeTypeField.select.value || !planField.select.value;
    }
    function resetPlanOptions() {
        resetSelect(planField.select, t('admin.sellSubscription.planPlaceholder'));
    }
    function fillPlanOptions(placeTypeId) {
        resetPlanOptions();
        for (const plan of activePlans.filter((p) => p.placeTypeId === placeTypeId)) {
            appendOption(planField.select, plan.id, t('admin.sellSubscription.planOption', {
                name: plan.name,
                duration: formatDurationFromMinutes(plan.hours * 60),
                price: plan.price.toFixed(2),
            }));
        }
    }
    // Also used to refresh the panel after a sale, so the admin sees the balance
    // the customer's confirmation email quotes rather than the pre-sale one.
    function loadCustomerInfo(customer) {
        customerInfo.hidden = false;
        customerInfo.textContent = t('admin.sellSubscription.loading');
        fetchCustomerSubscriptions(customer.id)
            .then((subscriptions) => {
            renderCustomerInfo(customerInfo, customer, subscriptions);
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                ctx.showLogin();
                return;
            }
            console.error('Failed to load customer subscriptions:', err);
            customerInfo.innerHTML = '';
            customerInfo.textContent = t('admin.sellSubscription.subscriptionsLoadError');
        });
    }
    customerField.select.addEventListener('change', () => {
        success.hidden = true;
        customerInfo.hidden = true;
        customerInfo.innerHTML = '';
        placeTypeField.select.selectedIndex = 0;
        resetPlanOptions();
        updateSubmitState();
        const customer = customers.find((c) => c.id === Number(customerField.select.value));
        if (!customer)
            return;
        loadCustomerInfo(customer);
    });
    placeTypeField.select.addEventListener('change', () => {
        success.hidden = true;
        fillPlanOptions(Number(placeTypeField.select.value));
        updateSubmitState();
    });
    planField.select.addEventListener('change', () => {
        success.hidden = true;
        updateSubmitState();
    });
    submit.addEventListener('click', () => {
        const customer = customers.find((c) => c.id === Number(customerField.select.value));
        const plan = activePlans.find((p) => p.id === Number(planField.select.value));
        const placeType = placeTypes.find((pt) => pt.id === Number(placeTypeField.select.value));
        if (!customer || !plan || !placeType)
            return;
        openConfirmModal(ctx, customer, plan, placeType, () => {
            success.textContent = t('admin.sellSubscription.success');
            success.hidden = false;
            resetPlanOptions();
            updateSubmitState();
            loadCustomerInfo(customer);
        });
    });
}
function renderCustomerInfo(container, customer, subscriptions) {
    container.innerHTML = '';
    const nameEl = document.createElement('p');
    nameEl.className = 'admin__sell-name';
    nameEl.textContent = `${customer.surname} ${customer.name}`;
    container.appendChild(nameEl);
    if (subscriptions.length === 0) {
        const noSubs = document.createElement('p');
        noSubs.className = 'admin__sell-no-subs';
        noSubs.textContent = t('admin.sellSubscription.noActiveSubscriptions');
        container.appendChild(noSubs);
        return;
    }
    const list = document.createElement('ul');
    list.className = 'admin__sell-subs-list';
    // One row per subscription (not merged like the customer email's summary):
    // the admin is about to sell another one and needs to see what's already
    // there individually, including which workspace each one is tied to.
    for (const sub of subscriptions) {
        const li = document.createElement('li');
        li.textContent = t('admin.sellSubscription.subscriptionLine', {
            plan: sub.planName,
            place: sub.placeTypeName,
            duration: formatDurationFromMinutes(sub.leftMinutes),
        });
        list.appendChild(li);
    }
    container.appendChild(list);
}
function openConfirmModal(ctx, customer, plan, placeType, onSold) {
    const overlay = document.createElement('div');
    overlay.className = 'admin__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'admin__modal admin__modal--confirm';
    const text = document.createElement('p');
    text.className = 'admin__modal-confirm-text';
    text.textContent = t('admin.sellSubscription.confirmText', {
        name: `${customer.surname} ${customer.name}`,
        plan: plan.name,
        hours: String(plan.hours),
        place: placeType.name,
    });
    const error = document.createElement('p');
    error.className = 'admin__error';
    error.hidden = true;
    const actions = document.createElement('div');
    actions.className = 'admin__modal-confirm-actions';
    const noButton = document.createElement('button');
    noButton.type = 'button';
    noButton.className = 'cta admin__submit';
    noButton.textContent = t('admin.sellSubscription.confirmNo');
    noButton.addEventListener('click', () => overlay.remove());
    const yesButton = document.createElement('button');
    yesButton.type = 'button';
    yesButton.className = 'cta admin__submit';
    yesButton.textContent = t('admin.sellSubscription.confirmYes');
    yesButton.addEventListener('click', () => {
        error.hidden = true;
        yesButton.disabled = true;
        noButton.disabled = true;
        yesButton.classList.add('is-loading');
        sellSubscription(customer.id, plan.id)
            .then(() => {
            overlay.remove();
            onSold();
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                overlay.remove();
                ctx.showLogin();
                return;
            }
            console.error('Failed to sell subscription:', err);
            error.textContent =
                err instanceof ApiError
                    ? t('admin.sellSubscription.submitError')
                    : t('admin.sellSubscription.genericError');
            error.hidden = false;
            yesButton.disabled = false;
            noButton.disabled = false;
            yesButton.classList.remove('is-loading');
        });
    });
    actions.append(noButton, yesButton);
    modal.append(text, error, actions);
    overlay.appendChild(modal);
    ctx.section.appendChild(overlay);
}
